import { useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Wallet, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { useWalletSystem } from '@/hooks/useWalletSystem';
import { useUser } from '@/contexts/UserContext';
import { useQueryClient } from '@tanstack/react-query';

interface PayWithWalletDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: 'invoice' | 'sourcing';
  referenceNumber: string;
  amount: number;
  onSuccess?: () => void;
}

export function PayWithWalletDialog({
  open,
  onOpenChange,
  type,
  referenceNumber,
  amount,
  onSuccess
}: PayWithWalletDialogProps) {
  const { user } = useUser();
  const { wallet, isLoadingWallet, payInvoice, paySourcingRequest, isPayingInvoice, isPayingSourcingRequest } = useWalletSystem();
  const queryClient = useQueryClient();
  
  const isPaying = isPayingInvoice || isPayingSourcingRequest;

  // Refresh wallet data when dialog opens
  useEffect(() => {
    if (open && user?.customerCode) {
      queryClient.invalidateQueries({ queryKey: ['wallet', user.customerCode] });
    }
  }, [open, user?.customerCode, queryClient]);

  // Everything is USD now - no currency conversion needed
  const amountToPay = amount;

  // Check if wallet has sufficient balance (USD only)
  // Support both balance_usd and balanceUsd field names
  const usdBalance = parseFloat(wallet?.balance_usd || wallet?.balanceUsd || wallet?.balanceUSD || '0');
  const hasSufficientBalance = usdBalance >= amountToPay;

  const handlePayment = () => {
    if (!user?.customerCode || !hasSufficientBalance) return;

    const paymentData = {
      customerCode: user.customerCode,
      currency: 'USD',
      amount: amountToPay,
    };

    if (type === 'invoice') {
      payInvoice(
        { ...paymentData, invoiceNumber: referenceNumber },
        {
          onSuccess: () => {
            onOpenChange(false);
            onSuccess?.();
          }
        }
      );
    } else {
      paySourcingRequest(
        { ...paymentData, requestNumber: referenceNumber },
        {
          onSuccess: () => {
            onOpenChange(false);
            onSuccess?.();
          }
        }
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            Pay with Wallet
          </DialogTitle>
          <DialogDescription>
            Pay {type === 'invoice' ? 'invoice' : 'sourcing request'} {referenceNumber} from your wallet
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Payment Amount */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <Label className="text-sm text-blue-900">Amount to Pay</Label>
            <p className="text-2xl font-bold text-blue-900 mt-1">${amountToPay.toFixed(2)} USD</p>
          </div>

          {/* Loading State */}
          {isLoadingWallet ? (
            <div className="flex items-center justify-center py-4">
              <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground mr-2" />
              <span className="text-muted-foreground">Loading wallet balance...</span>
            </div>
          ) : (
            <>
              {/* Balance Check */}
              {!hasSufficientBalance ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Insufficient USD balance. You need $
                    {(amountToPay - usdBalance).toFixed(2)} more to complete this payment.
                    <br />
                    <br />
                    Please add credit to your wallet.
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    Your USD balance is sufficient for this payment.
                    <br />
                    Remaining balance after payment:{' '}
                    <strong>
                      ${(usdBalance - amountToPay).toFixed(2)}
                    </strong>
                  </AlertDescription>
                </Alert>
              )}

              {/* Current Balance */}
              <div className="p-3 border rounded-lg">
                <Label className="text-xs text-muted-foreground">Your Wallet Balance</Label>
                <p className="text-lg font-semibold">${usdBalance.toFixed(2)} USD</p>
              </div>
            </>
          )}

          {/* Payment Button */}
          <Button
            onClick={handlePayment}
            disabled={!hasSufficientBalance || isPaying || isLoadingWallet}
            className="w-full"
            size="lg"
          >
            {isPaying ? 'Processing Payment...' : `Pay $${amountToPay.toFixed(2)}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
